import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Check, ChevronDown, Pin, Plus, X } from 'lucide-react'
import { cn } from './cn'
import { CountBadge } from './count-badge'
import { usePopoverDismiss } from './use-popover-dismiss'
import { usePopoverPlacement } from './use-popover-placement'

export interface DropdownProps {
  /** aria-label for the trigger — the field's name, not the current value. */
  label: string
  /** The trigger text. `null` shows `placeholder` instead. */
  value: string | null
  placeholder?: string
  /** Extra selected/bound values beyond the one shown — renders a `+N` badge. */
  more?: number
  /**
   * A 14px mark trailing the trigger text — a state the value itself cannot
   * carry (e.g. a pin saying this device was chosen by hand). The caller owns
   * its icon AND its colour; the shell only gives it a slot.
   */
  mark?: React.ReactNode
  /** Render `value` in the monospace face (e.g. a captured key combo). */
  mono?: boolean
  /** Widens the panel for content that needs more room (e.g. an ordered list). */
  wide?: boolean
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * Called before Escape closes the panel. Return `false` to let the panel
   * handle Escape itself (e.g. cancel an in-progress recording) instead of
   * closing.
   */
  onEscape?: () => boolean
  /** Panel content — built from the row/action primitives below. */
  children: React.ReactNode
  className?: string
}

const MIN_WIDTH = 220
const WIDE_MIN_WIDTH = 288

/**
 * Dropdown shell shared by every "pick one, pick several, or reorder a list"
 * control on settings pages: opens/closes, positions the panel, owns
 * pointerdown-outside / Escape / focus-return and the trigger's three
 * states — it knows nothing about hotkeys, microphones, or language, and
 * takes no i18n strings (the caller supplies every label). Its interaction
 * contract mirrors `Picker` (pointerdown, not mousedown, for outside-close;
 * containment checks rather than stopPropagation) so the two controls feel
 * identical on the same page.
 */
export function Dropdown({
  label,
  value,
  placeholder,
  more = 0,
  mark,
  mono,
  wide,
  disabled,
  onOpenChange,
  onEscape,
  children,
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  // `children` is deliberately NOT a recalc dep: the panel content is a fresh
  // element tree on every parent render, so it re-ran the placement effect on
  // each one. The hook observes the content element's size instead, which is
  // the change that actually matters here (a row, alert or hint appearing).
  const [position, resetPosition] = usePopoverPlacement(
    open,
    { trigger: buttonRef, panel: panelRef, scrollArea: scrollAreaRef, content: contentRef },
    [wide],
    { minWidth: wide ? WIDE_MIN_WIDTH : MIN_WIDTH }
  )

  const close = useCallback(
    (refocus = true) => {
      setOpen(false)
      resetPosition()
      onOpenChange?.(false)
      if (refocus) buttonRef.current?.focus()
    },
    [onOpenChange, resetPosition]
  )

  const openPanel = useCallback(() => {
    setOpen(true)
    onOpenChange?.(true)
  }, [onOpenChange])

  usePopoverDismiss(open, {
    containers: [rootRef],
    onPointerDownOutside: () => close(false),
    onEscapeKey: () => {
      if (onEscape && onEscape() === false) return
      close(true)
    },
  })

  return (
    <div className={cn('relative w-full', className)} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        disabled={disabled}
        className={cn(
          'flex h-[var(--ctl-h)] w-full items-center justify-between gap-2 rounded-[var(--radius)] border border-border bg-background px-3 text-left text-base text-foreground transition-colors focus:border-foreground focus:outline-2 focus:outline-[var(--fg-soft)] disabled:cursor-not-allowed disabled:opacity-40',
          open && 'border-foreground outline-2 outline-[var(--fg-soft)]'
        )}
        onClick={() => (open ? close() : openPanel())}
      >
        <span className={cn('flex min-w-0 flex-1 items-center gap-2 truncate', value === null && 'text-muted')}>
          <span className={cn('truncate', mono && value !== null && 'font-mono text-sm')}>
            {value ?? (placeholder ?? '')}
          </span>
          {more > 0 ? <CountBadge count={more} prefix="+" /> : null}
          {mark ? <span className="flex size-3.5 shrink-0 items-center justify-center">{mark}</span> : null}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn('size-4 shrink-0 text-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={label}
          className="fixed z-[70] flex flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-background p-1 shadow-[var(--shadow)]"
          style={
            position
              ? {
                  left: position.left,
                  width: position.width,
                  top: position.top,
                  bottom: position.bottom,
                  maxHeight: position.maxHeight,
                }
              : { visibility: 'hidden' }
          }
        >
          <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-y-auto">
            <div ref={contentRef} className="flex flex-col gap-0.5">
              {children}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export interface DropdownRowProps {
  text: string
  /** Renders `text` in the monospace face (e.g. a key combo). */
  mono?: boolean
  /** Leading index for an ordered list (e.g. microphone priority). */
  order?: number
  /** Trailing status text, e.g. availability. */
  sub?: string
  /** Trailing status NODE, for a status a string cannot carry (e.g. a live level meter). */
  subNode?: React.ReactNode
  /** Marks `sub` as an active/positive state instead of a neutral one. */
  subTone?: 'on'
  /** Trailing text shown only on hover/focus (e.g.「更換」). */
  hoverSub?: string
  /** Shows a trailing check mark — no highlight fill, per the design guideline. */
  selected?: boolean
  /** In progress (e.g. capturing a key): dims + pulses the label and grabs focus. */
  busy?: boolean
  /** Dims the leading `order` index — the list is not being followed right now. */
  dimOrder?: boolean
  ariaLabel?: string
  onClick?: () => void
  /** Trailing inline actions — usually one or more `DropdownIconButton`s. */
  tail?: React.ReactNode
  /**
   * Reveal `tail` only on row hover / keyboard focus inside the row, so a long
   * list of rows stays quiet. An `active` `DropdownIconButton` stays visible
   * regardless — it is showing state, not offering an action.
   */
  tailOnHover?: boolean
}

/** One selectable/actionable row inside a `Dropdown` panel. */
export function DropdownRow({
  text,
  mono,
  order,
  sub,
  subNode,
  subTone,
  hoverSub,
  selected,
  busy,
  dimOrder,
  ariaLabel,
  onClick,
  tail,
  tailOnHover,
}: DropdownRowProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (busy) buttonRef.current?.focus()
  }, [busy])

  return (
    <div className={cn('group flex items-center rounded-[var(--radius)]', busy ? 'bg-surface' : 'hover:bg-surface')}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel ?? text}
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-[var(--radius)] px-2 py-2 text-left text-sm text-foreground focus-visible:outline-none"
      >
        {order !== undefined ? (
          <span
            aria-hidden="true"
            className={cn(
              'w-4 shrink-0 text-right font-mono text-xs text-muted',
              dimOrder && 'opacity-40'
            )}
          >
            {order}
          </span>
        ) : null}
        <span className={cn('min-w-0 flex-1 truncate', mono && 'font-mono', busy && 'motion-safe:animate-pulse text-muted')}>
          {text}
        </span>
        {sub ? (
          <span className={cn('shrink-0 text-xs', subTone === 'on' ? 'text-foreground' : 'text-muted')}>{sub}</span>
        ) : null}
        {subNode ? <span className="shrink-0">{subNode}</span> : null}
        {hoverSub ? (
          <span className="shrink-0 text-xs text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            {hoverSub}
          </span>
        ) : null}
        {selected ? <Check aria-hidden="true" className="size-4 shrink-0" /> : null}
      </button>
      {tail ? (
        <span
          data-dropdown-tail
          className={cn(
            'flex shrink-0 items-center',
            // Hover/focus reveal is scoped to the SAME `:not([data-active])` set on
            // both sides, so the reveal always outranks the hide (one extra
            // `:hover`/`:focus-within` on the ancestor) instead of depending on
            // stylesheet order.
            tailOnHover &&
              '[&>button]:transition-opacity [&>button:not([data-active])]:opacity-0 group-hover:[&>button:not([data-active])]:opacity-100 group-focus-within:[&>button:not([data-active])]:opacity-100'
          )}
        >
          {tail}
        </span>
      ) : null}
    </div>
  )
}

export type DropdownIconButtonKind = 'up' | 'down' | 'remove' | 'pin' | 'add'

const ICON_BUTTON_ICONS: Record<DropdownIconButtonKind, typeof ArrowUp> = {
  up: ArrowUp,
  down: ArrowDown,
  remove: X,
  pin: Pin,
  add: Plus,
}

export interface DropdownIconButtonProps {
  kind: DropdownIconButtonKind
  label: string
  disabled?: boolean
  /**
   * The action is currently in effect (e.g. this row is the pinned one): the
   * icon fills in, turns brand red and stays visible even inside a
   * `tailOnHover` row, because it is reporting state rather than offering an
   * action. It is meant to be the only colour in the panel.
   */
  active?: boolean
  onClick?: () => void
}

/** Trailing icon action on a `DropdownRow` — reorder, remove, pin or add. */
export function DropdownIconButton({
  kind,
  label,
  disabled,
  active,
  onClick,
}: DropdownIconButtonProps) {
  const Icon = ICON_BUTTON_ICONS[kind]
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active === undefined ? undefined : active}
      data-active={active ? '' : undefined}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'grid size-7 shrink-0 place-items-center rounded-[var(--radius-sm)] transition-colors focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] disabled:cursor-not-allowed disabled:opacity-30',
        active
          ? 'text-brand hover:bg-brand-soft hover:text-brand-hover'
          : 'text-muted hover:bg-surface hover:text-foreground'
      )}
    >
      <Icon
        aria-hidden="true"
        className="size-3.5"
        strokeWidth={2}
        fill={active ? 'currentColor' : 'none'}
      />
    </button>
  )
}

export interface DropdownAddRowProps {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
}

/** Trailing "add" affordance — e.g.「設定按鍵」／「加入第二組按鍵」. */
export function DropdownAddRow({ children, onClick, disabled }: DropdownAddRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2 rounded-[var(--radius)] px-2 py-2 text-left text-sm text-muted transition-colors hover:bg-surface hover:text-foreground focus-visible:bg-surface focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Plus aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
}

/** Hairline divider between panel sections. */
export function DropdownSeparator() {
  return <div role="separator" className="mx-1 my-1 h-px bg-border" />
}

export interface DropdownHeadingProps {
  children: React.ReactNode
}

/** Small-caps section label above a group of rows (e.g. addable devices). */
export function DropdownHeading({ children }: DropdownHeadingProps) {
  return (
    <p className="mx-1 mb-0.5 mt-2 text-2xs font-medium uppercase tracking-[0.08em] text-muted">{children}</p>
  )
}

export interface DropdownHintProps {
  children: React.ReactNode
}

/** Explains a step the panel can't otherwise convey (e.g. how to cancel a capture). */
export function DropdownHint({ children }: DropdownHintProps) {
  return <p className="mx-1 mb-1.5 mt-0.5 text-xs leading-relaxed text-muted">{children}</p>
}

export interface DropdownStatusProps {
  children: React.ReactNode
  /** Optional inline text action, e.g.「改回依優先順序」. */
  actionLabel?: string
  onAction?: () => void
}

/**
 * One line at the top of a panel explaining why the panel's list is NOT being
 * followed right now, with an optional way back. Only render it when that is
 * the case — an always-present explanation is noise.
 */
export function DropdownStatus({ children, actionLabel, onAction }: DropdownStatusProps) {
  return (
    <div className="mx-1 mb-1 mt-0.5 flex items-center justify-between gap-2 text-xs text-muted">
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {actionLabel ? (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 rounded-[var(--radius-sm)] text-xs text-foreground underline decoration-border underline-offset-2 transition-colors hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)]"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  )
}

export interface DropdownAlertProps {
  children: React.ReactNode
}

/** Inline error inside the panel — the panel stays open while it shows. */
export function DropdownAlert({ children }: DropdownAlertProps) {
  return (
    <p role="alert" className="mx-1 my-1.5 flex items-center gap-1.5 text-sm text-error">
      <span
        aria-hidden="true"
        className="grid size-4 shrink-0 place-items-center rounded-full border border-current font-mono text-3xs"
      >
        !
      </span>
      <span>{children}</span>
    </p>
  )
}
